// v2 eagle — a raptor mid-soar: a long dark body with a darker mantle across
// the back, a proud white head on a white neck wedge with fierce yellow eyes
// and the hooked golden beak (a curled dark tip), a broad white fanned tail and
// big layered wings with three separated finger feathers each. Two liveries:
// bald eagle (white head/tail) and golden eagle (tawny nape, banded tail).
// Ported 1:1 from age-of-mcp's v2/eagle.ts, using the library's shared
// `wildland` (wingPair) helper.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { body: "#3a2a1a", chest: "#46341f", mantle: "#2e2114", head: "#f2ede1",
    beak: "#e8a91e", tail: "#efe9dc", band: false },
  { body: "#4a3014", chest: "#553a1a", mantle: "#3a2610", head: "#8a6228",
    beak: "#c89a2a", tail: "#463318", band: true },
];

function eagleParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // long body, breast mass, dark mantle across the back
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.body,
      position: [-0.005, 0.032, 0], scale: [1.7, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.chest,
      position: [0.038, 0.026, 0], scale: [1.0, 1.0, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.mantle,
      position: [-0.02, 0.05, 0], scale: [1.3, 0.6, 1.0] },
    // white neck wedge + head
    { geometry: new THREE.IcosahedronGeometry(0.018, 0), color: c.head,
      position: [0.06, 0.044, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0235, 0), color: c.head,
      position: [0.075, 0.054, 0], scale: [1.2, 1.0, 0.95] },
    // hooked golden beak with a curled dark tip
    { geometry: new THREE.ConeGeometry(0.009, 0.026, 4), color: c.beak,
      position: [0.102, 0.052, 0], rotation: [0, 0, -1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#8a5e10",
      position: [0.111, 0.0435, 0] },
    // broad fanned tail
    { geometry: new THREE.ConeGeometry(0.021, 0.062, 4), color: c.tail,
      position: [-0.092, 0.033, 0], rotation: [0, 0, Math.PI / 2],
      scale: [1, 1, 0.42] },
  ];
  if (c.band) {
    parts.push({ geometry: new THREE.BoxGeometry(0.01, 0.004, 0.03),
      color: c.mantle, position: [-0.118, 0.0335, 0] });
  }
  // fierce yellow eyes with dark pupils
  for (const s of [1, -1] as const) {
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: "#f4c83a",
        position: [0.085, 0.061, s * 0.0135] },
      { geometry: new THREE.IcosahedronGeometry(0.002, 0), color: "#1c1408",
        position: [0.0875, 0.061, s * 0.0145] },
    );
  }
  parts.push(...wildland.wingPair({
    root: [0.005, 0.042, 0.02], span: 0.165, chord: 0.06, rise: 0.028,
    sweep: 0.026, thick: 0.0068,
    inner: c.chest, outer: c.mantle, tip: "#1e160c", fingers: 3,
  }));
  return parts;
}

export const eagleV2 = defineModel({
  id: "eagle-v2",
  batchId: "fable",
  name: "Eagle (v2)",
  tags: ["creature","eagle","low-poly","age-of-mcp","fable"],
  summary: "Low-poly eagle.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.14,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => eagleParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => eagleParts(1) },
  ],
  animations: [],
});
